import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useSnackbar } from 'notistack';
import BankingService from 'services/banking.service';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState, setAccounts } from 'stores/banking';
import { EAccountStatus } from 'common/accounts.interface';
import { maskEmail } from 'utils/formatters';
import AccountStatusInfo from './components/AccountStatusInfo';
import { AccountPendingCard } from './Cards/AccountPendingCard';
import { AccountApprovedCard } from './Cards/AccountApprovedCard';

const Banking = () => {
  const dispatch = useDispatch();
  const bankingService = new BankingService();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();

  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await bankingService.getAccounts();
        dispatch(setAccounts(data));
      } catch (error: any) {
        if ('status' in error && error.status !== 404) {
          enqueueSnackbar('Erro ao buscar dados da conta bancária. Contate o suporte.', { variant: 'error' });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const paymentAccount = useMemo(() => {
    if (!accounts) return null;
    return accounts.find((account: any) => account.accountType === 'PAYMENT');
  }, [accounts]);

  const controlAccount = useMemo(() => {
    if (!accounts) return null;
    return accounts.find((account: any) => account.accountType === 'CONTROL');
  }, [accounts]);

  const firstParticipant = useMemo(() => {
    if (!paymentAccount || !paymentAccount.participants || !paymentAccount.participants.length) return null;
    return paymentAccount.participants[0];
  }, [paymentAccount]);

  const paymentParticipantMaskedEmail = useMemo(() => {
    if (!firstParticipant) return '';
    return maskEmail(firstParticipant.email);
  }, [firstParticipant]);

  useEffect(() => {
    if (!loading && paymentAccount?.status === EAccountStatus.ACTIVE && controlAccount?.status === EAccountStatus.ACTIVE) {
      navigate('/banking/dashboard', { replace: true });
    }
  }, [loading, paymentAccount, controlAccount, navigate]);

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '100vh' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && !paymentAccount && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '100vh' }}>
          <AccountStatusInfo
            type="awaitingAccountOpening"
            btnTxt="Abrir conta digital"
            description="Clique <a href='https://torqi-public.s3.us-east-1.amazonaws.com/material_abertura_conta.pdf' target='_blank' rel='noopener noreferrer' style='color:#1976d2;text-decoration:underline;'>aqui</a> e conheça a Conta Digital"
            btnRoute="/banking/form"
          />
        </Box>
      )}

      {!loading &&
        [
          EAccountStatus.PENDING,
          EAccountStatus.AWAITING_PROPOSAL_CREATION,
          EAccountStatus.REJECTED_BACKGROUND_CHECK,
          EAccountStatus.AWAITING_DOCUMENT_VALIDATION,
        ].includes(paymentAccount?.status as EAccountStatus) && (
          <AccountPendingCard
            participant={{
              name: firstParticipant?.fullName || '',
              email: paymentParticipantMaskedEmail || '',
            }}
          />
        )}

      {!loading && paymentAccount?.status === EAccountStatus.APPROVED && controlAccount?.status !== EAccountStatus.APPROVED && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '100vh' }}>
          <AccountStatusInfo
            type="awaitingKyc"
            description="Só estamos aguardando a <span style='color:#1976d2;'>conclusão da verificação de identidade</span>, o<br />link foi enviado para o seu e-mail usado para a criação da conta<br />Bank no cadastro.<br /><br />Assim que a sua identidade for confirmada, você poderá acessar a<br />sua conta no Bank Torqi."
            icon={<BadgeIcon sx={{ color: '#115398', fontSize: 32 }} />}
            btnColor="#FFF"
          />
        </Box>
      )}

      {!loading && paymentAccount?.status === EAccountStatus.APPROVED && controlAccount?.status === EAccountStatus.APPROVED && (
        <AccountApprovedCard />
      )}

      {!loading && paymentAccount?.status === EAccountStatus.REJECTED_DOCUMENT && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '100vh' }}>
          <AccountStatusInfo
            type="kycError"
            description="Ocorreu um erro na verificação dos seus documentos, mas não se<br />preocupe, <b>você pode fazer a verificação novamente.</b><br>Em breve você receberá um novo e-mail para continuar o processo."
            icon={<ReportProblemIcon sx={{ color: '#C8004C', fontSize: 32 }} />}
            iconBg="#FDECEE"
            customStyles={{ mb: 5, mt: 5 }}
          />
        </Box>
      )}
    </>
  );
};

export default Banking;
